import React, { createContext, useContext, useEffect, useMemo, useCallback } from 'react'
import { useLingui } from '@lingui/react/macro'
import { useFragment, graphql } from 'react-relay'
import type { GestureResponderEvent } from 'react-native'
import Animated, {
  useSharedValue,
  useAnimatedStyle,
  withSequence,
  withTiming,
  withDelay,
  Easing,
} from 'react-native-reanimated'

import { useMedia } from 'tamagui'

// import { EmojiIcon } from '../../components/EmojiIcon/EmojiIcon'
import { Icon } from '../Icon'
import { Button } from '../Button/Button'

const AnimateChevronContext = createContext(false)
export const AnimateChevronProvider = ({ children }: { children: React.ReactNode }) => (
  <AnimateChevronContext.Provider value={true}>{children}</AnimateChevronContext.Provider>
)

import { EventAttendButtonButton_event$key } from './__generated__/EventAttendButtonButton_event.graphql'

const BUTTON_HEIGHT = 30

export type BUTTON_ACTIONS =
  | 'Attend'
  | 'Attending'
  | 'Waiting'
  | 'Pending'
  | 'Checkout'
  | 'Next Time'

const AttendChevron = () => {
  const translateX = useSharedValue(0)
  const { reducedMotion: reducedEffects } = useMedia()

  useEffect(() => {
    if (reducedEffects) return

    const ease = Easing.inOut(Easing.ease)
    const bounce = () => {
      translateX.value = withSequence(
        // First bounce
        withTiming(3, { duration: 200, easing: ease }),
        withTiming(0, { duration: 150, easing: ease }),
        // Brief pause
        withDelay(100, withSequence(
          // Second bounce
          withTiming(3, { duration: 200, easing: ease }),
          withTiming(0, { duration: 150, easing: ease }),
        )),
      )
    }

    const interval = setInterval(bounce, 8000)
    return () => clearInterval(interval)
  }, [reducedEffects])

  const animatedStyle = useAnimatedStyle(() => ({
    transform: [{ translateX: translateX.value }],
  }))

  return (
    <Animated.View style={animatedStyle}>
      <Icon key='notAttending' name='chevron-right' color='$colorOnPrimary' size='$4' />
    </Animated.View>
  )
}

const BUTTON_ATTENDANCE_ICONS: Record<BUTTON_ACTIONS, React.ReactNode> = {
  Attending: <Icon key='attending' name='checkmark' color='$colorStrong' size='$4' />,
  Attend: <Icon key='notAttending' name='chevron-right' color='$colorOnPrimary' size='$4' />,
  Waiting: <Icon key='waiting' name='clock' color='$colorStrong' size='$4' />,
  Pending: <Icon key='pending' name='clock' color='$colorMuted' size='$4' />,
  Checkout: (
    // <EmojiIcon size={4} name='admission-tickets' />

    <Icon
      key='waiting'
      name='ticket-diagonal-filled'
      color='$colorOnPrimary'
      size='$4'
      //   rotate='-45deg'
    />
  ),
  'Next Time': null,
}

export const EventAttendButtonButton = Button.styleable<{
  event: EventAttendButtonButton_event$key
}>(({ event, onPress, ...props }, ref) => {
  const { t } = useLingui()
  const animateChevron = useContext(AnimateChevronContext)
  const data = useFragment(
    graphql`
      fragment EventAttendButtonButton_event on Event {
        id
        name

        endAt

        myAttendance {
          visibleStatus
          paymentStatus
        }

        eventTicketingTiers(orderBy: [SORT_ORDER_ASC, CREATED_AT_ASC]) {
          totalCount
        }
      }
    `,
    event
  )

  const eventInPast = useMemo(() => {
    if (!data.endAt) {
      return false
    }

    return new Date().getTime() > new Date(data.endAt as string).getTime()
  }, [data.endAt])

  const buttonAction = useMemo<BUTTON_ACTIONS>(() => {
    if (eventInPast) {
      return 'Next Time'
    }

    if (
      data.myAttendance?.visibleStatus &&
      ['ATTENDING_IN_PERSON', 'ATTENDING_VIRTUAL'].includes(
        data.myAttendance.visibleStatus
      )
    ) {
      return 'Attending'
    }

    if (data.myAttendance?.visibleStatus === 'PENDING_APPROVAL') {
      return 'Pending'
    }

    if (
      data.eventTicketingTiers.totalCount &&
      data.myAttendance?.paymentStatus !== 'SUCCEEDED'
    ) {
      return 'Checkout'
    }

    if (!data.myAttendance || data.myAttendance.visibleStatus === 'NOT_ATTENDING') {
      return 'Attend'
    }

    return 'Waiting'
  }, [data, eventInPast])

  const buttonLabel = useMemo(() => {
    switch (buttonAction) {
      case 'Attend':
        return t`Attend`
      case 'Attending':
        return t`Attending`
      case 'Pending':
        return t`Pending`
      case 'Checkout':
        return t`Checkout`
      case 'Next Time':
        return t`Next Time`
      case 'Waiting':
        return t`Waiting`
    }
  }, [buttonAction])

  const ariaLabel = useMemo(() => {
    switch (buttonAction) {
      case 'Attend':
        return `Attend ${data.name}`
      case 'Attending':
        return `You're attending ${data.name}`
      case 'Pending':
        return `Your registration for ${data.name} is pending approval`
      case 'Checkout':
        return `Purchase ticket for ${data.name}`
      case 'Next Time':
        return `Notify me about future events like ${data.name}`
      case 'Waiting':
        return `You're waiting a spot to become available for ${data.name}`
    }
  }, [buttonAction, data.name])

  const handlePress = useCallback(
    (event: GestureResponderEvent) => {
      if (onPress) {
        return onPress(event)
      }
    },
    [onPress]
  )

  return (
    <Button
      ref={ref}
      height={BUTTON_HEIGHT}
      minHeight={BUTTON_HEIGHT}
      borderRadius={14}
      paddingHorizontal='$2'
      hoverStyle={{ scale: 1.02 }}
      {...props}
      secondary={!['Attend', 'Checkout'].includes(buttonAction)}
      onPress={handlePress}
      gap='$1'
      aria-label={ariaLabel}
    >
      {buttonAction === 'Attend' && animateChevron
        ? <AttendChevron />
        : BUTTON_ATTENDANCE_ICONS[buttonAction]}

      <Button.Text
        size='$4'
        marginLeft={BUTTON_ATTENDANCE_ICONS[buttonAction] ? 0 : '$1'}
        marginRight='$1'
      >
        {buttonLabel}
      </Button.Text>
    </Button>
  )
})
